import {
	type ColumnDef,
	type ColumnFiltersState,
	getCoreRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	type Row,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";
import { useLocalStorage, useSessionStorage } from "@uidotdev/usehooks";
import { type RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { trackTableFilterChanged } from "../../../analytics";
import { detectFilterChanges } from "../FilterableDataTable/utils";
import { type DynamicDataTableColumn } from "./types";
import { getInitialColumnSizing } from "./utils/getInitialColumnSizing";
import { getProportionalColumnSizing } from "./utils/getProportionalColumnSizing";
import { reconcileEnforcedColumnFilters } from "./utils/reconcileEnforcedColumnFilters";
import { toTanStackColumnDefs } from "./utils/toTanStackColumnDefs";

const EMPTY_FILTERS: ColumnFiltersState = [];
const EMPTY_SORTING: SortingState = [];

const sortingStorageKey = (name: string) => `${name}_server_v1_sorting`;
const filtersStorageKey = (name: string) => `${name}_server_v1_filters`;

const readSessionValue = <T>(key: string, fallback: T): T => {
	try {
		const raw = window.sessionStorage.getItem(key);
		return raw === null ? fallback : (JSON.parse(raw) as T);
	} catch {
		return fallback;
	}
};

/** Seeds query state before mount so restored filters do not trigger an initial unfiltered request. */
export const readPersistedServerTableState = (
	name: string,
	defaultSorting: SortingState = EMPTY_SORTING,
): { columnFilters: ColumnFiltersState; sorting: SortingState } => ({
	columnFilters: readSessionValue(filtersStorageKey(name), EMPTY_FILTERS),
	sorting: readSessionValue(sortingStorageKey(name), defaultSorting),
});
const NEAR_BOTTOM_PX = 200;

export type UseServerDynamicDataTableStateParams<TData extends object> = {
	name: string;
	columns: readonly DynamicDataTableColumn<TData>[];
	rows: TData[];
	totalCount: number;
	columnVisibility?: VisibilityState;
	defaultColumnFilters?: ColumnFiltersState;
	/** Reconciles externally driven columns without clearing user filters on other columns. */
	enforcedColumnFilters?: ColumnFiltersState;
	initialSorting?: SortingState;
	onFiltersChange?: (filters: ColumnFiltersState) => void;
	onSortingChange?: (sorting: SortingState) => void;
	onLoadMore?: () => void;
	isLoadingNextPage?: boolean;
};

export type UseServerDynamicDataTableStateResult<TData extends object> = {
	table: ReturnType<typeof useReactTable<TData>>;
	rows: Row<TData>[];
	columnDefs: ColumnDef<TData, unknown>[];
	rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
	parentRef: RefObject<HTMLDivElement | null>;
	fitColumnWidthsToContainer: () => void;
	columnFilters: ColumnFiltersState;
	sorting: SortingState;
};

export function useServerDynamicDataTableState<TData extends object>({
	name,
	columns,
	rows,
	totalCount,
	columnVisibility,
	defaultColumnFilters,
	enforcedColumnFilters,
	initialSorting,
	onFiltersChange,
	onSortingChange,
	onLoadMore,
	isLoadingNextPage,
}: UseServerDynamicDataTableStateParams<TData>): UseServerDynamicDataTableStateResult<TData> {
	const tanStackColumnDefs = useMemo(() => toTanStackColumnDefs(columns), [columns]);
	const [dataTableStorage, setDataTableStorage] = useLocalStorage<Record<string, number>>(
		`inrev_dynamic_table_${name}`,
		{},
	);

	useEffect(() => {
		setDataTableStorage((current) => getInitialColumnSizing(tanStackColumnDefs, current));
	}, [tanStackColumnDefs, setDataTableStorage]);

	const [sorting, setSorting] = useSessionStorage<SortingState>(
		sortingStorageKey(name),
		initialSorting ?? EMPTY_SORTING,
	);
	const [columnFilters, setColumnFiltersRaw] = useSessionStorage<ColumnFiltersState>(
		filtersStorageKey(name),
		EMPTY_FILTERS,
	);

	const onFiltersChangeRef = useRef(onFiltersChange);
	onFiltersChangeRef.current = onFiltersChange;
	const onSortingChangeRef = useRef(onSortingChange);
	onSortingChangeRef.current = onSortingChange;

	// Avoid state updaters here: StrictMode can invoke them twice, duplicating analytics and callbacks.
	const columnFiltersRef = useRef(columnFilters);
	columnFiltersRef.current = columnFilters;
	const sortingRef = useRef(sorting);
	sortingRef.current = sorting;

	useEffect(() => {
		onFiltersChangeRef.current?.(columnFilters ?? EMPTY_FILTERS);
		onSortingChangeRef.current?.(sorting ?? EMPTY_SORTING);
	}, []);

	const setColumnFilters = useCallback(
		(updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
			const safePrev = columnFiltersRef.current ?? EMPTY_FILTERS;
			const next = typeof updater === "function" ? updater(safePrev) : updater;

			setColumnFiltersRaw(next);

			for (const change of detectFilterChanges(safePrev, next)) {
				trackTableFilterChanged(name, change.columnId, change.values);
			}
			onFiltersChangeRef.current?.(next);
		},
		[name, setColumnFiltersRaw],
	);

	const setSortingWithCallback = useCallback(
		(updater: SortingState | ((old: SortingState) => SortingState)) => {
			const safePrev = sortingRef.current ?? EMPTY_SORTING;
			const next = typeof updater === "function" ? updater(safePrev) : updater;

			setSorting(next);
			onSortingChangeRef.current?.(next);
		},
		[setSorting],
	);

	const hasAppliedDefault = useRef(false);
	useEffect(() => {
		if (hasAppliedDefault.current || !defaultColumnFilters?.length) return;
		if (columnFilters.length === 0) {
			setColumnFilters(defaultColumnFilters);
		}
		hasAppliedDefault.current = true;
	}, [defaultColumnFilters, setColumnFilters, columnFilters]);

	const [lastAppliedEnforcedKey, setLastAppliedEnforcedKey] = useSessionStorage<string | null>(
		`${name}_server_v1_enforced_key`,
		null,
	);
	useEffect(() => {
		if (enforcedColumnFilters === undefined) {
			if (lastAppliedEnforcedKey !== null) setLastAppliedEnforcedKey(null);
			return;
		}
		const key = JSON.stringify(enforcedColumnFilters);
		if (key === lastAppliedEnforcedKey) return;
		setLastAppliedEnforcedKey(key);
		setColumnFilters((prev) => reconcileEnforcedColumnFilters(prev, enforcedColumnFilters));
	}, [enforcedColumnFilters, lastAppliedEnforcedKey, setLastAppliedEnforcedKey, setColumnFilters]);

	const parentRef = useRef<HTMLDivElement>(null);
	const emptyTableData = useMemo((): TData[] => [], []);

	const table = useReactTable<TData>({
		data: rows.length > 0 ? rows : emptyTableData,
		columns: tanStackColumnDefs,
		defaultColumn: {
			enableResizing: false,
			enableSorting: false,
			enableHiding: false,
		},
		state: {
			sorting,
			columnVisibility,
			columnFilters,
			columnSizing: dataTableStorage,
		},
		renderFallbackValue: "--",
		manualFiltering: true,
		manualSorting: true,
		enableMultiSort: false,
		onSortingChange: setSortingWithCallback,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		// Required for header filters when no server facet values are supplied.
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		onColumnSizingChange: setDataTableStorage,
		autoResetAll: false,
	});

	const { rows: tableRows } = table.getRowModel();

	const rowVirtualizer = useVirtualizer({
		count: tableRows.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 58,
		overscan: 20,
	});

	const onLoadMoreRef = useRef(onLoadMore);
	onLoadMoreRef.current = onLoadMore;
	const isLoadingNextPageRef = useRef(isLoadingNextPage);
	isLoadingNextPageRef.current = isLoadingNextPage;
	const rowCountRef = useRef(tableRows.length);
	rowCountRef.current = tableRows.length;
	const totalCountRef = useRef(totalCount);
	totalCountRef.current = totalCount;

	// Latch synchronously because two scroll events can arrive before loading state re-renders.
	const loadMoreDispatchedRef = useRef(false);

	useEffect(() => {
		loadMoreDispatchedRef.current = false;
		const el = parentRef.current;
		if (!el) return;

		const check = () => {
			if (loadMoreDispatchedRef.current || isLoadingNextPageRef.current) return;
			if (rowCountRef.current >= totalCountRef.current) return;
			if (el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX) {
				loadMoreDispatchedRef.current = true;
				onLoadMoreRef.current?.();
			}
		};

		check();

		el.addEventListener("scroll", check, { passive: true });
		return () => el.removeEventListener("scroll", check);
	}, [tableRows.length, totalCount, isLoadingNextPage]);

	const fitColumnWidthsToContainer = useCallback(() => {
		const el = parentRef.current;
		if (!el) return;
		const availableWidth = el.clientWidth;
		if (availableWidth <= 0) return;
		const visibleColumnDefs = table.getVisibleFlatColumns().map((c) => c.columnDef) as ColumnDef<
			TData,
			unknown
		>[];
		if (visibleColumnDefs.length === 0) return;
		const next = getProportionalColumnSizing(visibleColumnDefs, availableWidth);
		setDataTableStorage((prev) => ({ ...prev, ...next }));
	}, [setDataTableStorage, table]);

	return {
		table,
		rows: tableRows,
		columnDefs: tanStackColumnDefs,
		rowVirtualizer,
		parentRef,
		fitColumnWidthsToContainer,
		columnFilters,
		sorting,
	};
}
